import type { CurrencyRate } from './currency-rate.model';

const PREFERRED_CURRENCY_ORDER = [
  'USD',
  'EUR',
  'GBP',
  'SAR',
  'QAR',
  'KWD',
  'BHD',
  'OMR',
  'CHF',
  'CAD',
  'AUD',
  'SGD',
];

/**
 * Converts a marketplace amount into the display currency.
 * `change` is the number of local units for 1 foreign unit (`localValue` is usually 1).
 * Rounding follows `decimals`. `roundingFactor` is ignored so centimes stay visible
 * when the till reports a factor of 1.
 */
export function convertMarketplaceAmountToDisplay(
  amount: number,
  rate: Pick<CurrencyRate, 'localValue' | 'change' | 'decimals'>,
  options: { amountInCents?: boolean } = {},
): number {
  const major = options.amountInCents ? amount / 100 : amount;
  if (!rate.change) {
    return major;
  }

  const foreign = (major * (rate.localValue || 1)) / rate.change;
  const decimals = Number.isFinite(rate.decimals) ? Math.max(0, Math.trunc(rate.decimals)) : 2;
  const factor = 10 ** decimals;
  return Math.round(foreign * factor) / factor;
}

/** Legal line: ISO code plus amount, e.g. `AED 32.00`. */
export function formatLegalCurrencyAmount(
  amountMajor: number,
  currency: string | undefined,
): string {
  const code = (currency || 'EUR').toUpperCase();
  const digits = code === 'HUF' ? 0 : 2;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amountMajor);
  return `${code} ${formatted}`;
}

export function sortCurrencyRates(rates: readonly CurrencyRate[]): CurrencyRate[] {
  return [...rates].sort((left, right) => {
    const leftRank = rankCurrency(left.code);
    const rightRank = rankCurrency(right.code);
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    return left.code.localeCompare(right.code);
  });
}

export function currencyNarrowSymbol(code: string, locale: string): string {
  const normalized = code?.trim().toUpperCase();
  if (!normalized) {
    return '';
  }
  try {
    const parts = new Intl.NumberFormat(locale || 'en', {
      style: 'currency',
      currency: normalized,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find(part => part.type === 'currency')?.value ?? normalized;
  } catch {
    return normalized;
  }
}

export function currencyDisplayName(code: string, locale: string): string {
  const normalized = code?.trim().toUpperCase();
  if (!normalized) {
    return '';
  }
  try {
    return (
      new Intl.DisplayNames([locale || 'en'], { type: 'currency' }).of(normalized) ?? normalized
    );
  } catch {
    return normalized;
  }
}

export function formatExchangeRateDate(validFrom: string, locale: string): string {
  const date = new Date(validFrom);
  if (Number.isNaN(date.getTime())) {
    return validFrom;
  }
  return new Intl.DateTimeFormat(locale || 'en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function rankCurrency(code: string): number {
  const index = PREFERRED_CURRENCY_ORDER.indexOf(code);
  return index === -1 ? PREFERRED_CURRENCY_ORDER.length : index;
}
